import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { reload } from 'firebase/auth';
import { auth } from '../services/firebase';
import {
  onAuthStateChange,
  handleGoogleRedirect,
  hasGoogleRedirectPending,
  hasPendingGoogleLegalConsent,
  syncGoogleLegalConsentPendingAfterSignIn,
} from '../services/auth';
import { loadUserProfileDoc } from '../services/loadUserProfileDoc';
import { writeDisplayNameOnboardingDoneCache } from '../services/displayNameOnboardingCache';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { withTimeout } from '../utils/withTimeout';
import i18n from '../i18n';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AUTH_INIT_SAFETY_MS = 15000;

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [userDocReady, setUserDocReady] = useState(false);
  const [userProfileUnavailable, setUserProfileUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  /**
   * Гейт для Google-пользователей без принятых Terms/Privacy: не редиректить с / и не пускать в /app,
   * пока sync с Firestore не завершён (иначе лендинг успевает уйти в приложение до sessionStorage).
   */
  const [postGoogleLegalNavGate, setPostGoogleLegalNavGate] = useState({
    ready: true,
    needsConsent: false,
  });
  /** Первый колбэк onAuthStateChanged после подписки — снимаем блокировку UI (без гонки с getRedirectResult). */
  const authFirstEmitRef = useRef(false);
  const loadedUidRef = useRef(null);
  const userDocReadyRef = useRef(false);

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    const safetyTimer = window.setTimeout(() => {
      if (cancelled) return;
      setLoading((prev) => {
        if (prev) {
          console.warn('[AuthContext] Auth init timed out; showing app anyway');
        }
        return false;
      });
    }, AUTH_INIT_SAFETY_MS);

    (async () => {
      try {
        let redirectHandled = await withTimeout(
          handleGoogleRedirect(),
          8000,
          'handleGoogleRedirect timed out'
        );
        // На мобильных после возврата из OAuth redirect-результат иногда приходит с задержкой.
        if (!redirectHandled.user && !redirectHandled.error && hasGoogleRedirectPending()) {
          await new Promise((r) => setTimeout(r, 900));
          redirectHandled = await withTimeout(
            handleGoogleRedirect(),
            8000,
            'handleGoogleRedirect retry timed out'
          );
        }
      } catch (error) {
        console.error('[AuthContext] handleGoogleRedirect:', error);
      }
      if (cancelled) return;

      unsubscribe = onAuthStateChange(async (user) => {
        const isFirstEmit = !authFirstEmitRef.current;

        if (user) {
          setPostGoogleLegalNavGate({ ready: false, needsConsent: false });
          setCurrentUser(user);

          const baseUserData = {
            uid: user.uid,
            email: user.email,
            displayName: user.displayName,
            photoURL: user.photoURL,
          };
          const sameUid = loadedUidRef.current === user.uid;
          setUserData((prev) => {
            if (prev && prev.uid === user.uid) {
              return {
                ...prev,
                email: user.email,
                photoURL: user.photoURL,
                displayName: prev.displayName || user.displayName,
              };
            }
            return baseUserData;
          });
          if (!sameUid || !userDocReadyRef.current) {
            setUserDocReady(false);
            userDocReadyRef.current = false;
          }

          try {
            await withTimeout(
              syncGoogleLegalConsentPendingAfterSignIn(user.uid),
              4000,
              'legal consent sync timed out'
            );
          } catch (e) {
            console.warn('[AuthContext] syncGoogleLegalConsentPendingAfterSignIn', e);
          }
          setPostGoogleLegalNavGate({
            ready: true,
            needsConsent: hasPendingGoogleLegalConsent(),
          });

          if (isFirstEmit) {
            authFirstEmitRef.current = true;
            setLoading(false);
            window.clearTimeout(safetyTimer);
          }

          try {
            const result = await loadUserProfileDoc(user.uid);
            if (result.status === 'ok') {
              setUserData({
                ...baseUserData,
                ...result.data,
              });
              setUserProfileUnavailable(false);
              if (result.data.displayNameOnboardingDone === true) {
                writeDisplayNameOnboardingDoneCache(user.uid);
              }
            } else {
              setUserProfileUnavailable(result.status === 'unavailable');
            }
          } catch (error) {
            console.warn('[AuthContext] Firestore user doc exception:', error);
            setUserProfileUnavailable(true);
          } finally {
            loadedUidRef.current = user.uid;
            userDocReadyRef.current = true;
            setUserDocReady(true);
          }
        } else {
          setPostGoogleLegalNavGate({ ready: true, needsConsent: false });
          setCurrentUser(null);
          setUserData(null);
          loadedUidRef.current = null;
          userDocReadyRef.current = false;
          setUserDocReady(false);
          setUserProfileUnavailable(false);
          if (isFirstEmit) {
            authFirstEmitRef.current = true;
            setLoading(false);
            window.clearTimeout(safetyTimer);
          }
        }
      });
    })();

    return () => {
      cancelled = true;
      window.clearTimeout(safetyTimer);
      unsubscribe();
    };
  }, []);

  const refreshAuthUser = useCallback(async () => {
    const u = auth.currentUser;
    if (!u) return null;
    await reload(u);
    const fresh = auth.currentUser;
    setCurrentUser(fresh);
    return fresh;
  }, []);

  const markPostGoogleLegalComplete = useCallback(() => {
    setPostGoogleLegalNavGate({ ready: true, needsConsent: false });
  }, []);

  const value = {
    currentUser,
    userData,
    userDocReady,
    userProfileUnavailable,
    loading,
    setUserData,
    refreshAuthUser,
    postGoogleLegalNavGate,
    markPostGoogleLegalComplete,
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <LoadingSpinner message={i18n.t('auth.sessionLoading')} />
      ) : null}
      {!loading && children}
    </AuthContext.Provider>
  );
};
